import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, UserCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatBRL, formatDateTime } from "@/lib/format";

const STATUSES = [
  { value: "new", label: "Novo" },
  { value: "contacted", label: "Contatado" },
  { value: "converted", label: "Convertido" },
  { value: "discarded", label: "Descartado" },
];

const STATUS_TONE = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  contacted: "bg-blue-100 text-blue-800 border-blue-200",
  converted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  discarded: "bg-stone-200 text-stone-700",
};

export default function AdminLeads() {
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/leads");
      setItems(data);
    } catch (err) {
      console.error("Failed to load leads", err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Lead atualizado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const totalSavings = items.reduce(
    (acc, l) => acc + (Number(l.estimated_annual_savings) || 0),
    0
  );
  const newCount = items.filter((l) => l.status === "new").length;
  const convertedCount = items.filter((l) => l.status === "converted").length;

  return (
    <Layout title="Leads" subtitle="Capturas da Calculadora ROI e canais de marketing">
      <div data-testid="admin-leads" className="space-y-5">
        <section className="grid sm:grid-cols-3 gap-4">
          <Card className="p-5 rounded-xl border-stone-200 bg-white">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">Total de leads</div>
            <div className="mt-2 font-display text-3xl font-extrabold">{items.length}</div>
          </Card>
          <Card className="p-5 rounded-xl border-stone-200 bg-white">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">Novos</div>
            <div className="mt-2 font-display text-3xl font-extrabold">{newCount}</div>
            <div className="text-xs text-stone-500 font-body">
              {convertedCount} convertidos · {convertedCount && items.length ? ((convertedCount / items.length) * 100).toFixed(0) : 0}% taxa
            </div>
          </Card>
          <Card className="p-5 rounded-xl border-stone-200 bg-stone-950 text-white">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400 font-body">Pipeline ROI calculado</div>
            <div className="mt-2 font-display text-3xl font-extrabold text-emerald-400">{formatBRL(totalSavings)}</div>
            <div className="text-xs text-stone-400 font-body">economia anual prometida aos leads</div>
          </Card>
        </section>

        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="font-body">Contato</TableHead>
                <TableHead className="font-body">Operação</TableHead>
                <TableHead className="font-body">Economia prevista</TableHead>
                <TableHead className="font-body">Status</TableHead>
                <TableHead className="font-body">Capturado em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-stone-500 py-10">
                    <Sparkles className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                    Nenhum lead capturado ainda
                  </TableCell>
                </TableRow>
              )}
              {items.map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                  <TableCell>
                    <div className="font-display font-semibold flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-emerald-700" /> {l.name || "—"}
                    </div>
                    <div className="text-xs text-stone-500 font-body">{l.email}</div>
                    {l.phone && <div className="text-xs text-stone-500 font-body">{l.phone}</div>}
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    <div>{l.units} unidade{l.units !== 1 ? "s" : ""}</div>
                    <div className="text-xs text-stone-500">Faturamento: {formatBRL(l.monthly_revenue)}/mês</div>
                  </TableCell>
                  <TableCell className="font-display font-bold text-emerald-700">
                    {formatBRL(l.estimated_annual_savings)}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={l.status}
                      onValueChange={(v) => setStatus(l.id, v)}
                    >
                      <SelectTrigger className="w-36 h-9 bg-white" data-testid={`lead-status-${l.id}`}>
                        <Badge variant="outline" className={STATUS_TONE[l.status] || ""}>
                          {STATUSES.find((s) => s.value === l.status)?.label || l.status}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="font-body text-sm">{formatDateTime(l.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </Layout>
  );
}
